import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card } from 'src/app/components/Elements/Card';
import { Divider } from 'src/app/components/Elements/Divider';
import { PaymentCardItem } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { segmentTrack, setMedicationLoadingState, setShowNewArchivedPageModal } from 'src/app/state/actions';
import { archiveItem, fetchHomescreen, loadHomescreenMedications } from 'src/app/state/actions/homescreen';
import { getMedicationLoadingState, getPatients } from 'src/app/state/selectors';
import { loadArchivedMedications } from 'src/app/state/slices/archivedMedications';
import { useDispatch, useSelector } from 'src/app/state/store';
import type { HomePrescriptionItem, ToggleMedicationCardSections, PrescriptionLifecycle } from 'src/app/types';
import { REFILL_STATUSES } from 'src/consts';
import { MEDICATION_CARD } from 'src/consts/testIDs';
import { theme } from 'src/styles/theme';

import { MedicationContents } from '../container';
import styles from '../styles.module.scss';

import { ArchiveBannerButton } from './ArchiveBannerButton';
import { StatusSection } from './StatusSection';

export interface MedicationCardRendererProps extends ToggleMedicationCardSections {
  homeScreenItem: HomePrescriptionItem;
  isArchivedSection: boolean;
  hasMultiplePatients: boolean;
  addTopMarginToAllCards?: boolean;
  goToMedDetailsOnMedNameClick?: boolean;
  withLegacyAnimations?: boolean;
  showArchiveButton?: boolean;
  prescriptionLifecycle?: PrescriptionLifecycle;
  showDetailedStatusTracker?: boolean;
  showPrescriptionDates?: boolean;
  showCta?: boolean;
}

export const MedicationCard = ({
  homeScreenItem,
  prescriptionLifecycle,
  isArchivedSection,
  goToMedDetailsOnMedNameClick,
  showSecondaryName,
  showInstructions,
  showCheckout,
  showDosage,
  showMenuActions,
  showPatientName,
  addTopMarginToAllCards = true,
  showAddToBag,
  withLegacyAnimations = true,
  showArchiveButton = true,
  showDetailedStatusTracker = false,
  showPrescriptionDates = false,
  showCta = true,
}: MedicationCardRendererProps) => {
  const { showArchivedCTA, patientMedicationId, refillStatusText, medicationId } = homeScreenItem || {};
  const [trackArchiveMedication] = useLazySegmentTrack(UNIFIED.archiveMedication);
  const [trackUnarchiveMedication] = useLazySegmentTrack(UNIFIED.unarchiveMedication);
  const allPatients = useSelector(getPatients);
  const hasMultiplePatients = allPatients?.length > 1;
  const medicationLoadingState = useSelector(getMedicationLoadingState);

  const dispatch = useDispatch();

  // Controls when animations are expanded/collapsed, use for both card and inner card animations
  const [animationState, setAnimationState] = useState({ collapse: false, expand: true });
  const [overflowHidden, setOverflowHidden] = useState(false);
  useEffect(() => {
    if (withLegacyAnimations) {
      setOverflowHidden(true);
      setAnimationState({ collapse: false, expand: true });
      setTimeout(() => {
        setAnimationState(animationState => ({ ...animationState, expand: false }));
        setOverflowHidden(false);
      }, 500);
    }
  }, [withLegacyAnimations]);

  const isLoading = !!medicationLoadingState?.medications[patientMedicationId];

  const toggleArchiveButtonClicked = useCallback(async () => {
    const isExpired = refillStatusText === REFILL_STATUSES.EXPIRED;
    //setIsLoading(true);
    dispatch(setMedicationLoadingState(patientMedicationId, true));
    const response = await dispatch(archiveItem(patientMedicationId, isArchivedSection));
    dispatch(setShowNewArchivedPageModal(true));
    if (response) {
      await dispatch(loadHomescreenMedications());
      await dispatch(fetchHomescreen());
      await dispatch(loadArchivedMedications());
      if (isArchivedSection) {
        trackUnarchiveMedication({
          source: 'Medication Card Button',
          patientmedication_id: patientMedicationId,
          medication_id: medicationId,
          is_expired: isExpired,
        });
      } else {
        trackArchiveMedication({
          source: 'Medication Card Button',
          patientmedication_id: patientMedicationId,
          medication_id: medicationId,
          is_expired: isExpired,
        });
      }

      if (withLegacyAnimations) {
        setAnimationState({ collapse: true, expand: false });
        setOverflowHidden(false);
      }
    }
    dispatch(setMedicationLoadingState(patientMedicationId, false));
  }, [
    dispatch,
    isArchivedSection,
    medicationId,
    patientMedicationId,
    refillStatusText,
    trackArchiveMedication,
    trackUnarchiveMedication,
    withLegacyAnimations,
  ]);

  const { inView: statusTrackerInView, ref: statusTrackerRef } = useInView({ threshold: 0.25 });

  useEffect(() => {
    if (statusTrackerInView) {
      dispatch(
        segmentTrack(UNIFIED.viewStatusTrackerDetails, {
          patient_medications_id: patientMedicationId,
          state: refillStatusText,
        })
      );
    }
  }, [dispatch, statusTrackerInView, patientMedicationId, refillStatusText]);

  return (
    <Card
      className={classNames(
        styles.component,
        styles.overflow,
        overflowHidden && styles.noOverflow,
        withLegacyAnimations && animationState.collapse && styles.cardCollapse,
        withLegacyAnimations && animationState.expand && styles.cardExpand,
        isLoading && styles.isloading,
        (isArchivedSection || (!isArchivedSection && showArchivedCTA)) && showArchiveButton && styles.withArchivedButton
      )}
      dataTestid={MEDICATION_CARD}
      addTopMarginToAllCards={addTopMarginToAllCards}
    >
      {
        <PaymentCardItem className={styles.innerCard}>
          {homeScreenItem && (
            <MedicationContents
              isArchivedSection={isArchivedSection}
              homeScreenItem={homeScreenItem}
              hasMultiplePatients={hasMultiplePatients}
              goToMedDetailsOnMedNameClick={goToMedDetailsOnMedNameClick}
              showMenuActions={showMenuActions}
              showCheckout={showCheckout}
              showDosage={showDosage}
              showSecondaryName={showSecondaryName}
              showInstructions={showInstructions}
              setAnimationState={setAnimationState}
              setOverflowHidden={setOverflowHidden}
              animationState={animationState}
              showPatientName={showPatientName}
              showAddToBag={showAddToBag}
              withLegacyAnimations={withLegacyAnimations}
              showPrescriptionDates={showPrescriptionDates}
              rootPrescriptionLifecycle={prescriptionLifecycle}
              showCta={showCta}
            />
          )}
        </PaymentCardItem>
      }

      {showArchiveButton && showArchivedCTA && !isArchivedSection && (
        <ArchiveBannerButton
          animateCollapse={withLegacyAnimations ? animationState.collapse : false}
          onClick={toggleArchiveButtonClicked}
          isArchived={false}
          isLoading={false}
        />
      )}
      {showArchiveButton && isArchivedSection && (
        <ArchiveBannerButton
          animateCollapse={withLegacyAnimations ? animationState.collapse : false}
          onClick={toggleArchiveButtonClicked}
          isArchived={true}
          isLoading={false}
        />
      )}

      {showDetailedStatusTracker && prescriptionLifecycle && (
        <div
          className={classNames(
            styles.animationContainer,
            withLegacyAnimations && animationState?.collapse && styles.innerCardCollapse,
            withLegacyAnimations && animationState?.expand && styles.innerCardExpand
          )}
          ref={statusTrackerRef}
        >
          <Divider margin={`${theme.space.s4} ${theme.space.s5}`} backgroundColor="capsuleGreen00" />

          <StatusSection prescriptionLifecycle={prescriptionLifecycle} />
        </div>
      )}
    </Card>
  );
};
